import React, { useState } from 'react';
import {
    createStyles,
    FormControl,
    IconButton,
    Input,
    InputAdornment,
    InputLabel,
    InputProps,
    makeStyles,
    Typography
} from '@material-ui/core';
// import Visibility from "@material-ui/icons/Visibility";
// import VisibilityOff from "@material-ui/icons/VisibilityOff";
// import inputStyles from "./styles/inputStyles";
import { FieldMetaProps, useField } from 'formik';
import clsx from 'clsx';
import { Visibility, VisibilityOff } from 'src/assets/icons/common/Visibility';
import { createResponsiveFontSize } from 'src/utils/helpers/theme';

export interface BaseInputProps extends InputProps {
    label: string;
    name: string;
    type?: string;
    disabled?: boolean;
    classes?: AppClassNameProps;
    startAdornment?: React.ReactNode;
    theme?: AppTheme;
}

const useInputTheme = makeStyles((theme) =>
    createStyles({
        focused: {},
        disabled: {},
        lightLabelText: { color: '#000', '&$focused': { color: '#000' } },
        darkLabelText: { color: theme.palette.grey[200], '&$focused': { color: '#FFF' } },
        lightText: { color: '#000' },
        darkText: { color: '#FFF' },
        lightUnderline: {
            '&:after': { borderBottom: `2px solid #000` },
            '&:before': { borderBottom: `2px solid #000` },
            '&:hover:not($disabled):before': {
                borderBottom: `2px solid  #000`,
                '@media (hover: none)': { borderBottom: `1px solid #000` }
            }
        },
        darkUnderline: {
            '&:after': { borderBottom: `2px solid #FFF` },
            '&:before': { borderBottom: `2px solid ${theme.palette.grey[200]}` },
            '&:hover:not($disabled):before': {
                borderBottom: `2px solid  #FFF`,
                '@media (hover: none)': { borderBottom: `1px solid #FFF` }
            }
        }
    })
);
const useErrorStyle = makeStyles(
    createStyles({
        root: { marginTop: 4, ...createResponsiveFontSize(-1) },
        hiddenErrorMsg: { visibility: 'hidden' }
    })
);

export const ErrorText = ({ meta }: { meta: FieldMetaProps<any> }) => {
    const errorClasses = useErrorStyle();
    return (
        <Typography
            color="error"
            className={clsx(errorClasses.root, {
                [errorClasses.hiddenErrorMsg]: !meta.touched || !meta.error
            })}>
            {meta.error ? meta.error : 'no error'}
        </Typography>
    );
};
const useTheme = (theme?: AppTheme, classes?: AppClassNameProps) => {
    const inputClasses = useInputTheme();
    return theme === 'light'
        ? {
              labelText: clsx(inputClasses.lightLabelText, {
                  [classes?.label || '']: classes?.label
              }),
              inputText: clsx(inputClasses.lightText, { [classes?.input || '']: classes?.input }),
              underline: inputClasses.lightUnderline,
              focused: inputClasses.focused
          }
        : {
              labelText: clsx(inputClasses.darkLabelText, {
                  [classes?.label || '']: classes?.label
              }),
              inputText: clsx(inputClasses.darkText, { [classes?.input || '']: classes?.input }),
              underline: inputClasses.darkUnderline,
              focused: inputClasses.focused
          };
};

export const CustomTextInput = ({
    label,
    disabled,
    classes,
    theme,
    startAdornment,
    ...props
}: BaseInputProps) => {
    const [field, meta] = useField(props.name);
    // const classes = useInputClasses();
    const { labelText, inputText, underline, focused } = useTheme(theme, classes);

    return (
        <FormControl className={classes?.root}>
            <InputLabel classes={{ root: labelText, focused }} htmlFor={props.name}>
                {label}
            </InputLabel>
            <Input
                {...props}
                classes={{ input: inputText, underline }}
                error={Boolean(meta.touched && meta.error)}
                key={props.name}
                id={props.name}
                // type={props.type}
                startAdornment={startAdornment}
                {...field}
            />

            <ErrorText meta={meta} />
        </FormControl>
    );
};

export const CustomPasswordInput = ({ label, classes, theme, ...props }: BaseInputProps) => {
    const [field, meta] = useField(props.name);
    const [showPassword, handleClickShowPassword] = useState(false);
    const { labelText, inputText, underline, focused } = useTheme(theme, classes);
    return (
        <FormControl className={classes?.root}>
            <InputLabel classes={{ root: labelText, focused }} htmlFor={props.name}>
                {label}
            </InputLabel>
            <Input
                classes={{ input: inputText, underline }}
                error={Boolean(meta.touched && meta.error)}
                key={props.name}
                id={props.name}
                type={showPassword ? 'text' : 'password'}
                // {...InputProps}
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => handleClickShowPassword(!showPassword)}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                }
                {...field}
            />
            <ErrorText meta={meta} />
        </FormControl>
    );
};
